import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { Color, SvgIconTypeMap } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { styled } from "@mui/material/styles";
import FlexColumn from "designSystem/components/flex/FlexColumn";
import Grid from "designSystem/components/grid/Grid";
import Text from "designSystem/components/text/Text";
import React, { forwardRef, FunctionComponent, ReactNode } from "react";
import { blue, red, yellow } from "designSystem/styles/color";
import { GridProps } from "../grid/Grid";

enum Variant {
	banner = "banner",
	error = "error",
	info = "info",
	warning = "warning",
}

type ColorMapping = Record<Variant, Partial<Color>>;
type IconMapping = Record<Variant, OverridableComponent<SvgIconTypeMap>>;

const colorMapping: ColorMapping = {
	banner: red,
	error: red,
	info: blue,
	warning: yellow,
};

const VARIANTS: IconMapping = {
	banner: ErrorIcon,
	error: ErrorIcon,
	info: InfoIcon,
	warning: WarningIcon,
};

const PREFIX = "Message";

const classes = {
	buttons: `${PREFIX}buttons`,
	buttonsBanner: `${PREFIX}buttonsBanner`,
	iconBase: `${PREFIX}iconBase`,
	iconBanner: `${PREFIX}iconBanner`,
	list: `${PREFIX}list`,
	text: `${PREFIX}text`,
};

const StyledGrid = styled(Grid)(({
	theme,
}) => ({
	borderRadius: 3,
	display: "flex",
	flexDirection: "row",
	minHeight: theme.spacing(12),

	[`& .${classes.buttons}`]: {
		display: "flex",
		flexDirection: "row",
		marginLeft: 20,
		marginRight: theme.spacing(3),
		marginTop: 12,
		width: "auto",
	},
	[`& .${classes.buttonsBanner}`]: {
		marginRight: theme.spacing(6),
	},
	[`& .${classes.iconBase}`]: {
		height: 20,
		margin: 14,
		width: 20,
	},
	[`& .${classes.iconBanner}`]: {
		height: 24,
		marginLeft: theme.spacing(6),
		marginRight: theme.spacing(2),
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(3),
		width: 24,
	},
	[`& .${classes.list}`]: {
		paddingLeft: 22,
		marginTop: 8,
		marginBottom: 0,
	},
	[`& .${classes.text}`]: {
		maxWidth: "fit-content",
		textAlign: "start",
	},
}));

export interface MessageProps extends Omit<GridProps, "children"> {
	/** Details to show in bullet points */
	bulletPoints?: string[];

	/** Buttons to add in the message */
	children?: {
		buttons?: ReactNode;
	};

	/** Message description */
	description?: string;

	/** parameters to pass if looking up resource */
	titleResParams?: Array<number | string>;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	titleRes?: string;

	/** Text for message title  */
	titleText?: string;

	/** Message variant */
	variant: keyof typeof Variant;
};

const Message: FunctionComponent<MessageProps> = forwardRef((props, ref) => {
	const { bulletPoints, children, className, description, sx, titleResParams, titleRes, titleText, variant, ...otherProps } = props;
	const IconComponent = VARIANTS[variant];
	const isBanner = variant === Variant.banner;

	return (
		<StyledGrid
			{...otherProps}
			className={className || ""}
			ref={ref}
			sx={{
				backgroundColor: colorMapping[variant][50],
				...sx,
			}}
		>
			<IconComponent
				className={isBanner ? classes.iconBanner : classes.iconBase}
				sx={{
					color: colorMapping[variant][500],
				}}
			/>
			<FlexColumn
				flexGrow={1}
				mr={3}
				pb={3}
				pt={4}
			>
				<Text
					bold={bulletPoints?.length > 0 || !!description || variant === Variant.banner}
					className={classes.text}
					grow
					res={titleRes}
					resParams={titleResParams}
					text={titleText}
				/>
				{bulletPoints && (
					<Grid className={classes.text}>
						<ul className={classes.list}>
							{bulletPoints.map((c, index) => (
								<li key={index}>
									<Text text={c} />
								</li>
							))}
						</ul>
					</Grid>
				)}
				{description && (
					<Grid className={classes.text} style={{ marginTop: bulletPoints?.length ? 0 : 8 }}>
						<Text text={description} />
					</Grid>
				)}
			</FlexColumn>
			{children?.buttons && (
				<Grid className={`${classes.buttons} ${isBanner ? classes.buttonsBanner : ""}`}>
					{children.buttons}
				</Grid>
			)}
		</StyledGrid>
	);
});

export default Message;